import { Injectable } from '@angular/core';
import { BaseComponent } from '@wms/components';
import { BaseVariableService, VariableCrudManagerService } from '@wms/variables';
import {PageService, FileService, ProjectService} from '@wms/services';
import { find, forEach, reject } from 'lodash-es';
import { Subject } from 'rxjs';
import ts from "typescript/lib/tsserverlibrary";

@Injectable({
  providedIn: 'root'
})
export class WmsNavigationVariableService extends BaseComponent {
  loadedPages: any = {};
  propertyGroupChangeSubject = new Subject();
  isPrefabTemplate: boolean;

  constructor(private variableService: BaseVariableService, private pageService: PageService, private variableCrudManagerService: VariableCrudManagerService,
    private fileService: FileService) {
    super();
    this.isPrefabTemplate = this.projectService.isPrefabType();
  }

  __getVariableDependencies() {
    /* remove pageName property in case of prefab project */
    if (this.isPrefabTemplate) {
      let operationPropertyObj = this.variableService.selectedVariable.propertyObj,
        selectedOperation = this.variableService.selectedVariable.variableObject.operation;

      delete operationPropertyObj.operation.options.gotoPage;
      delete operationPropertyObj.operation.options.goToPreviousPage;
      selectedOperation = selectedOperation !== 'gotoPage' ? selectedOperation : 'gotoTab';
      this.variableService.selectedVariable.variableObject.operation = selectedOperation;
    }
    if (this.projectService.isNativeMobileApplicationType()) {
      let operationPropObj = this.variableService.selectedVariable.propertyObj;

      delete operationPropObj.operation.options.gotoTab;
      delete operationPropObj.operation.options.gotoAccordion;
      delete operationPropObj.operation.options.gotoSegment;
    }

    this.__onPropertyChange('operation', undefined, true);
  }

  /*
  * returns property object(s) to be displayed based on the operation provided
  */
  __getDataPropsForOperation(operation: any) {
    let tempPropArray: any = [],
      allPageTypesMap: any = {},
      projectPagesMap: any = {};

    /* create project pages map */
    forEach(this.pageService.getPages(), (pageObj: any) => {
      allPageTypesMap[pageObj.name] = pageObj.name;
      if (pageObj.type === 'PAGE') {
        projectPagesMap[pageObj.name] = pageObj.name;
      }
    });

    switch (operation) {
      case 'gotoPage':
        tempPropArray = [
          {
            target: 'pageName',
            type: 'string',
            widgettype: 'typeahead',
            options: projectPagesMap,
            useHelpTxt: true
          }
        ];
        break;
      case 'gotoTab':
        tempPropArray = [
          {
            target: 'pageName',
            widgettype: 'typeahead',
            type: 'string',
            options: allPageTypesMap,
            useHelpTxt: true
          },
          {
            target: 'tabName',
            widgettype: 'typeahead',
            type: 'string',
            useHelpTxt: true
          }
        ];
        break;
      case 'gotoAccordion':
        tempPropArray = [
          {
            target: 'pageName',
            widgettype: 'typeahead',
            type: 'string',
            options: allPageTypesMap,
            useHelpTxt: true
          },
          {
            target: 'accordionName',
            widgettype: 'typeahead',
            type: 'string',
            useHelpTxt: true
          }
        ];
        break;
      case 'gotoSegment':
        tempPropArray = [
          {
            target: 'pageName',
            widgettype: 'typeahead',
            type: 'string',
            options: allPageTypesMap,
            useHelpTxt: true
          },
          {
            target: 'segmentName',
            widgettype: 'typeahead',
            type: 'string',
            useHelpTxt: true
          }
        ];
        break;
    }

    /* in case of prefab, remove pageName property */
    return this.isPrefabTemplate ? reject(tempPropArray, { 'target': 'pageName' }) : tempPropArray;
  }

  __getBindingObject(property: any) {
    let variable = this.variableService.selectedVariable.variableObject,
      bindingObject = find(variable.dataBinding, (binding) => {
        return binding.target === property;
      });

    return bindingObject;
  }

  /*function to handle property change for a navigationCall*/
  __onPropertyChange(propertyName: any, value?: any, initFlag?: any) {
    let selectedVariable = this.variableService.selectedVariable,
      selectedOperation,
      dataPropsBySelectedOperation,
      pageNameBinding,
      selectedPage;
    if (!initFlag) {
      this.variableService.markUpdated();
    }
    /* If the 'operation' property changed, change the data properties */
    switch (propertyName.toLowerCase()) {
      case 'operation':
        selectedOperation = selectedVariable.variableObject['operation'];
        dataPropsBySelectedOperation = this.__getDataPropsForOperation(selectedOperation);

        /*check value sanity*/
        if (!selectedOperation) {
          return;
        }

        /* merge the existing bindings with new(required bindings) */
        this.variableService.prepareBindings(dataPropsBySelectedOperation);
        this.variableService.addBindingsToProperties();

        if (selectedOperation !== 'goToPreviousPage') {
          this.__onPropertyChange('pageName', undefined, true);

          // set required property to true.
          forEach(this.variableCrudManagerService.navigationTypes, (property) => {
            if (selectedVariable.propertyObj[property]) {
              selectedVariable.propertyObj[property].required = true;
            }
          });
        }
        break;
      case 'pagename':
        if (!initFlag) {
          forEach(['accordionName', 'tabName', 'viewName'], (property: any) => {
            if (selectedVariable.propertyObj[property]) {
              selectedVariable.propertyObj[property].modelObj[selectedVariable.propertyObj[property].modelKey] = '';
              selectedVariable.propertyObj[property].options = [];
            }
          });
        }
        if (this.isPrefabTemplate) {
          selectedPage = this.pageService.activePageName || 'Main';
        } else {
          /* put current page name by default for operation other than gotoPage */
          pageNameBinding = this.__getBindingObject('pageName');
          selectedPage = pageNameBinding.value;
        }
        selectedOperation = selectedVariable.variableObject['operation'];
        if (selectedOperation !== 'gotoPage') {
          this.__loadPageContainerElements(selectedPage);
        }
        if (!initFlag) {
          selectedVariable.variableObject.dataSet = [];
        }
        this.variableService.loadPageDataParams(selectedPage);
        break;
    }
    this.propertyGroupChangeSubject.next(true);
  };

  /*
  * loads the provided page and stores the container elements against it
  * passes the elements to setViewElements to set against respective properties
  */
  __loadPageContainerElements(page: any) {
    /*If page is not defined then returning*/
    if (!page) {
      return;
    }
    /* if not already loaded, load the required page */
    if (!this.loadedPages[page]) {
      this.fileService.read('pages/' + page + '/' + page + '.html').then((markup) => {
        markup = $(markup);
        this.loadedPages[page] = {
          'wm-view': markup.find('wm-view'),
          'wm-tabpane': markup.find('wm-tabpane'),
          'wm-accordionpane': markup.find('wm-accordionpane'),
          'wm-segment-content': markup.find('wm-segment-content')
        };
        this.__setViewElements(this.loadedPages[page]);
      })
    } else {
      this.__setViewElements(this.loadedPages[page]);
    }
  }

  /*
  * sets the container element names against respective properties
  */
  __setViewElements(elements: any) {
    let operation = this.variableService.selectedVariable.variableObject.operation,
      propertyObj = this.variableService.selectedVariable.propertyObj;

    switch (operation) {
      case 'gotoTab':
        propertyObj['tabName'].options = {};
        elements['wm-tabpane'].each((i: any, view: any) => {
          view = $(view);
          let viewName = view.attr('name');
          this.variableService.selectedVariable.propertyObj['tabName'].options[viewName] = viewName;
        });
        break;
      case 'gotoAccordion':
        propertyObj['accordionName'].options = {};
        elements['wm-accordionpane'].each((i: any, view: any) => {
          view = $(view);
          let viewName = view.attr('name');
          this.variableService.selectedVariable.propertyObj['accordionName'].options[viewName] = viewName;
        });
        break;
      case 'gotoSegment':
        propertyObj['segmentName'].options = {};
        elements['wm-segment-content'].each((i: any, view: any) => {
          view = $(view);
          let viewName = view.attr('name');
          this.variableService.selectedVariable.propertyObj['segmentName'].options[viewName] = viewName;
        });
        break;
    }
  }
}
